import { useMutation, useQuery } from "@apollo/client";
import React, { useMemo } from "react";

import {
  Alert,
  LoadingIndicator,
  SettingsPageContainer,
  useToast,
} from "../../../components";
import {
  CurrentUserFragment,
  OrganizationNotificationChannel as GqlOrganizationNotificationChannel,
  OrganizationNotificationSetting as GqlOrganizationNotificationSetting,
  OrganizationNotificationSettingsDocument,
  OrganizationNotificationType as GqlOrganizationNotificationType,
  UpdateOrganizationNotificationSettingDocument,
} from "../../graphql";
import {
  buildOrganizationNotificationSettingsMap,
  getOrganizationNotificationSetting,
  OrganizationNotificationChannel,
  OrganizationNotificationSetting,
  OrganizationNotificationSettingItem,
  OrganizationNotificationType,
} from "./notificationSettingsUtils";
import OrganizationNotificationsSettingsForm from "./OrganizationNotificationsSettingsForm";

interface OrganizationNotificationsSettingsProps {
  currentUser: CurrentUserFragment;
}

interface OrganizationNotificationSettingsQuery {
  currentUser: {
    organization: {
      id: string;
      organizationNotificationSettings: OrganizationNotificationSettingItem[];
    };
  };
}

const TO_GQL_ORGANIZATION_NOTIFICATION_TYPE: Record<
  OrganizationNotificationType,
  GqlOrganizationNotificationType
> = {
  INTERVIEW_RECAP: GqlOrganizationNotificationType.InterviewRecap,
  INTERVIEW_PREP: GqlOrganizationNotificationType.InterviewPrep,
  WEEKLY_RECAP: GqlOrganizationNotificationType.WeeklyRecap,
  MONTHLY_ANALYTICS: GqlOrganizationNotificationType.MonthlyAnalytics,
  CALL_SHARE: GqlOrganizationNotificationType.CallShare,
  CALL_NOTE: GqlOrganizationNotificationType.CallNote,
  MORNING_DIGEST: GqlOrganizationNotificationType.MorningDigest,
  COACHING: GqlOrganizationNotificationType.Coaching,
  CANDIDATE_REDISCOVERY: GqlOrganizationNotificationType.CandidateRediscovery,
};

const TO_GQL_ORGANIZATION_NOTIFICATION_CHANNEL: Record<
  OrganizationNotificationChannel,
  GqlOrganizationNotificationChannel
> = {
  EMAIL: GqlOrganizationNotificationChannel.Email,
  SLACK: GqlOrganizationNotificationChannel.Slack,
  TEAMS: GqlOrganizationNotificationChannel.Teams,
};

const TO_GQL_ORGANIZATION_NOTIFICATION_SETTING: Record<
  OrganizationNotificationSetting,
  GqlOrganizationNotificationSetting
> = {
  USER_CHOICE: GqlOrganizationNotificationSetting.UserChoice,
  FORCE_ENABLE: GqlOrganizationNotificationSetting.ForceEnable,
  FORCE_DISABLE: GqlOrganizationNotificationSetting.ForceDisable,
};

const OrganizationNotificationsSettings: React.FC<
  OrganizationNotificationsSettingsProps
> = ({ currentUser }) => {
  const toast = useToast();
  const settingsQuery = useQuery<OrganizationNotificationSettingsQuery>(
    OrganizationNotificationSettingsDocument
  );
  const [updateOrganizationNotificationSetting, updateSettingMutation] =
    useMutation(UpdateOrganizationNotificationSettingDocument, {
      onCompleted: () => {
        toast({
          title: "Settings saved",
          status: "success",
        });
      },
      onError: () => {
        toast({
          title: "Error updating organization notification settings",
          status: "error",
        });
      },
    });

  const settingsMap = useMemo(
    () =>
      buildOrganizationNotificationSettingsMap(
        settingsQuery.data?.currentUser?.organization
          ?.organizationNotificationSettings
      ),
    [settingsQuery.data]
  );

  if (!settingsQuery.data?.currentUser?.organization) {
    return (
      <SettingsPageContainer
        maxW="unset"
        heading="Notification preferences"
        subHeading="Set organization-wide rules for how notifications are delivered."
      >
        {settingsQuery.loading && <LoadingIndicator mt="25%" />}
        {settingsQuery.error && (
          <Alert
            mb={6}
            status="error"
            description={settingsQuery.error.message}
          />
        )}
      </SettingsPageContainer>
    );
  }

  return (
    <SettingsPageContainer
      maxW="unset"
      heading="Notification preferences"
      subHeading="Set organization-wide rules for how notifications are delivered."
    >
      <OrganizationNotificationsSettingsForm
        interviewRecapSubscriptionEmail={getOrganizationNotificationSetting(
          settingsMap,
          "INTERVIEW_RECAP",
          "EMAIL"
        )}
        interviewRecapSubscriptionSlack={getOrganizationNotificationSetting(
          settingsMap,
          "INTERVIEW_RECAP",
          "SLACK"
        )}
        interviewRecapSubscriptionTeams={getOrganizationNotificationSetting(
          settingsMap,
          "INTERVIEW_RECAP",
          "TEAMS"
        )}
        interviewPrepSubscriptionEmail={getOrganizationNotificationSetting(
          settingsMap,
          "INTERVIEW_PREP",
          "EMAIL"
        )}
        interviewPrepSubscriptionSlack={getOrganizationNotificationSetting(
          settingsMap,
          "INTERVIEW_PREP",
          "SLACK"
        )}
        interviewPrepSubscriptionTeams={getOrganizationNotificationSetting(
          settingsMap,
          "INTERVIEW_PREP",
          "TEAMS"
        )}
        weeklyRecapSubscriptionEmail={getOrganizationNotificationSetting(
          settingsMap,
          "WEEKLY_RECAP",
          "EMAIL"
        )}
        weeklyRecapSubscriptionSlack={getOrganizationNotificationSetting(
          settingsMap,
          "WEEKLY_RECAP",
          "SLACK"
        )}
        weeklyRecapSubscriptionTeams={getOrganizationNotificationSetting(
          settingsMap,
          "WEEKLY_RECAP",
          "TEAMS"
        )}
        monthlyAnalyticsSubscriptionEmail={getOrganizationNotificationSetting(
          settingsMap,
          "MONTHLY_ANALYTICS",
          "EMAIL"
        )}
        monthlyAnalyticsSubscriptionTeams={getOrganizationNotificationSetting(
          settingsMap,
          "MONTHLY_ANALYTICS",
          "TEAMS"
        )}
        callShareSubscriptionEmail={getOrganizationNotificationSetting(
          settingsMap,
          "CALL_SHARE",
          "EMAIL"
        )}
        callShareSubscriptionSlack={getOrganizationNotificationSetting(
          settingsMap,
          "CALL_SHARE",
          "SLACK"
        )}
        callShareSubscriptionTeams={getOrganizationNotificationSetting(
          settingsMap,
          "CALL_SHARE",
          "TEAMS"
        )}
        callNoteSubscriptionEmail={getOrganizationNotificationSetting(
          settingsMap,
          "CALL_NOTE",
          "EMAIL"
        )}
        callNoteSubscriptionSlack={getOrganizationNotificationSetting(
          settingsMap,
          "CALL_NOTE",
          "SLACK"
        )}
        callNoteSubscriptionTeams={getOrganizationNotificationSetting(
          settingsMap,
          "CALL_NOTE",
          "TEAMS"
        )}
        morningDigestSubscriptionEmail={getOrganizationNotificationSetting(
          settingsMap,
          "MORNING_DIGEST",
          "EMAIL"
        )}
        morningDigestSubscriptionSlack={getOrganizationNotificationSetting(
          settingsMap,
          "MORNING_DIGEST",
          "SLACK"
        )}
        morningDigestSubscriptionTeams={getOrganizationNotificationSetting(
          settingsMap,
          "MORNING_DIGEST",
          "TEAMS"
        )}
        coachingSubscriptionEmail={getOrganizationNotificationSetting(
          settingsMap,
          "COACHING",
          "EMAIL"
        )}
        coachingSubscriptionSlack={getOrganizationNotificationSetting(
          settingsMap,
          "COACHING",
          "SLACK"
        )}
        coachingSubscriptionTeams={getOrganizationNotificationSetting(
          settingsMap,
          "COACHING",
          "TEAMS"
        )}
        candidateRediscoverySubscriptionEmail={getOrganizationNotificationSetting(
          settingsMap,
          "CANDIDATE_REDISCOVERY",
          "EMAIL"
        )}
        candidateRediscoverySubscriptionTeams={getOrganizationNotificationSetting(
          settingsMap,
          "CANDIDATE_REDISCOVERY",
          "TEAMS"
        )}
        isLoading={settingsQuery.loading || updateSettingMutation.loading}
        error={settingsQuery.error ?? updateSettingMutation.error}
        onSettingChange={async (notificationType, channel, setting) => {
          try {
            await updateOrganizationNotificationSetting({
              variables: {
                notificationType:
                  TO_GQL_ORGANIZATION_NOTIFICATION_TYPE[notificationType],
                channel: TO_GQL_ORGANIZATION_NOTIFICATION_CHANNEL[channel],
                setting: TO_GQL_ORGANIZATION_NOTIFICATION_SETTING[setting],
              },
            });
            await settingsQuery.refetch();
          } catch {
            // Errors are handled in the mutation onError callback.
          }
        }}
      />
    </SettingsPageContainer>
  );
};

export default OrganizationNotificationsSettings;
